import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	Chip,
	IconButton,
	Paper,
	Popover,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Autocomplete,
	MenuItem
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HomeIcon from "@mui/icons-material/Home";
import { useAuth } from "../../../context/AuthContext";
import { authorizedUsersService } from "../../../firebase/authorization/authorizedUsersService";
import { savePartnerInvestment } from "../../../firebase/partnerInvestment/savePartnerInvestment";
import { deletePartnerInvestment } from "../../../firebase/partnerInvestment/deletePartnerInvestment";
import { usePartnerInvestments } from "../../../hooks/usePartnerInvestments";
import { useShopCash } from "../../../hooks/useShopCash";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { PartnerInvestmentData } from "../../../models/partnerInvestment";
import { formatDate } from "../../../utils/calculations";
import {
	buildPartnerInvestmentPayload,
	validatePartnerInvestmentDraft
} from "../../../utils/partnerInvestmentFlow";

const getDateInputValue = () => new Date().toLocaleDateString("en-CA");
const defaultStartDate = "2026-01-01";
const formatCurrency = (value: number) => `Rs. ${value.toFixed(2)}`;

export const PartnerInvestments = () => {
	const { user, userRole } = useAuth();
	const isAdmin = userRole === "admin";
	const navigate = useNavigate();
	const items = useSelector((state: RootState) => state.items.items);
	const { investments, summary, loading, fetchPartnerInvestments } =
		usePartnerInvestments();

	const currentDate = getDateInputValue();
	const [startDate, setStartDate] = useState(defaultStartDate);
	const [endDate, setEndDate] = useState(currentDate);
	const [partnerName, setPartnerName] = useState("");
	const [partnerOptions, setPartnerOptions] = useState<string[]>([]);
	const [fundingSource, setFundingSource] = useState<"partner" | "shopCash">(
		"partner"
	);
	const { cashMovements, fetchShopCashData } = useShopCash();
	const [shopCashRef, setShopCashRef] = useState("");
	const [amount, setAmount] = useState("");
	const [investmentDate, setInvestmentDate] = useState(currentDate);
	const [description, setDescription] = useState("");
	const [itemRows, setItemRows] = useState<{ itemKey: string }[]>([
		{ itemKey: "" }
	]);
	const [saving, setSaving] = useState(false);
	const [savingMessage, setSavingMessage] = useState("");
	const [itemPopoverAnchor, setItemPopoverAnchor] =
		useState<HTMLElement | null>(null);
	const [activeItemLinks, setActiveItemLinks] = useState<any[]>([]);
	const [copyMessage, setCopyMessage] = useState("");
	const closePopoverTimer = useRef<number | null>(null);

	useEffect(() => {
		fetchPartnerInvestments(startDate, endDate, items);
	}, [fetchPartnerInvestments, startDate, endDate, items]);

	useEffect(() => {
		const loadPartners = async () => {
			try {
				const activePartners =
					await authorizedUsersService.getActivePartners();
				const partners = activePartners
					.map((entry) => entry.displayName?.trim() || entry.email)
					.filter(Boolean) as string[];
				setPartnerOptions(partners);
			} catch (error) {
				console.error("Failed to load partners:", error);
				setPartnerOptions([]);
			}
		};
		void loadPartners();

		// load recent shop cash entries for linking
		void fetchShopCashData(defaultStartDate, currentDate);
	}, []);

	const handleSearch = () => {
		if (!startDate || !endDate) {
			alert("Please select both dates.");
			return;
		}
		if (startDate > endDate) {
			alert("Start date cannot be after end date.");
			return;
		}
		fetchPartnerInvestments(startDate, endDate, items);
	};

	const handleSave = async () => {
		const validationError = validatePartnerInvestmentDraft({
			fundingSource,
			partnerName,
			amount,
			investmentDate,
			description,
			shopCashRef
		});
		if (validationError) {
			alert(validationError);
			return;
		}

		const finalItemLinks = itemRows
			.map((r) => {
				const item = items.find((it) => it.key === r.itemKey);
				return {
					itemKey: r.itemKey,
					itemCode: item?.itemCode || "",
					itemName: item?.name || "",
					quantity: item?.availableQuantity || 0,
					itemPurchasePrice: item?.purchasePrice ?? 0
				};
			})
			.filter((l) => l.itemKey);

		if (finalItemLinks.length === 0) {
			alert("Please add at least one item.");
			return;
		}

		const investment: PartnerInvestmentData = buildPartnerInvestmentPayload(
			{
				fundingSource,
				partnerName,
				amount,
				investmentDate,
				description,
				itemLinks: finalItemLinks,
				shopCashRef,
				createdAt: new Date().toISOString(),
				userId: user?.uid || "unknown",
				username: user?.displayName?.trim() || user?.email || "Unknown"
			}
		);

		try {
			setSaving(true);
			setSavingMessage("Saving investment...");
			await savePartnerInvestment(investment);
			setPartnerName("");
			setAmount("");
			setInvestmentDate(currentDate);
			setDescription("");
			setItemRows([{ itemKey: "" }]);
			await fetchPartnerInvestments(startDate, endDate, items);
			setSavingMessage("Investment saved.");
		} catch (error) {
			console.error("Failed to save partner investment:", error);
			setSavingMessage("Failed to save investment.");
		} finally {
			setSaving(false);
		}
	};

	const getComputedStatus = (investment: any) => {
		if (investment.status === "settled") return "settled";
		if (!investment.itemLinks || investment.itemLinks.length === 0)
			return "active";
		const allConsumed = investment.itemLinks.every((link: any) => {
			const item = items.find((it) => it.key === link.itemKey);
			if (!item) return false;
			return (item.availableQuantity || 0) === 0;
		});
		return allConsumed ? "consumed" : "active";
	};

	const formatInvestmentItemLines = (itemLinks: any[]) =>
		itemLinks.map((link: any) => {
			const itemPrice = link.itemPurchasePrice;
			return `${link.itemCode ? `(${link.itemCode}) ` : ""}${link.itemName || "Unnamed item"} x${link.quantity ?? 0}${
				itemPrice != null ? ` | ${formatCurrency(itemPrice)}` : ""
			}`;
		});

	const openItemPopover = (
		event: React.MouseEvent<HTMLElement>,
		itemLinks: any[]
	) => {
		if (closePopoverTimer.current) {
			window.clearTimeout(closePopoverTimer.current);
			closePopoverTimer.current = null;
		}
		setActiveItemLinks(itemLinks);
		setItemPopoverAnchor(event.currentTarget);
		setCopyMessage("");
	};

	const closeItemPopover = () => {
		closePopoverTimer.current = window.setTimeout(() => {
			setItemPopoverAnchor(null);
			setActiveItemLinks([]);
			setCopyMessage("");
		}, 150);
	};

	const cancelClosePopover = () => {
		if (closePopoverTimer.current) {
			window.clearTimeout(closePopoverTimer.current);
			closePopoverTimer.current = null;
		}
	};

	const handleCopyItems = async () => {
		const textToCopy =
			formatInvestmentItemLines(activeItemLinks).join("\n");
		try {
			await navigator.clipboard.writeText(textToCopy);
			setCopyMessage("Copied all items to clipboard");
		} catch (error) {
			console.error("Copy failed:", error);
			setCopyMessage(
				"Unable to copy automatically. Please use manual selection."
			);
		}
	};

	const itemPopoverOpen = Boolean(itemPopoverAnchor);

	const getShopCashLabel = (investment: any) => {
		if (investment.fundingSource !== "shopCash") return null;
		const mv = cashMovements.find(
			(m: any) => m.id === investment.shopCashRef
		);
		if (!mv) return "Shop Cash";
		return `${formatDate(mv.movementDate)} - ${mv.description || mv.expenseCategory || "Shop Cash"}`;
	};

	return (
		<Box sx={{ p: { xs: 2, md: 3 }, display: "grid", gap: 3 }}>
			<Paper sx={{ p: 3 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 2,
						gap: 1
					}}
				>
					<Typography variant="h5">Investment Tracker</Typography>
					<Button
						variant="contained"
						startIcon={<HomeIcon />}
						onClick={() => navigate("/")}
					>
						Go to Home
					</Button>
				</Box>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mb: 2 }}
				>
					Track stock funded by partners or shop cash in one place.
				</Typography>
				<Box
					sx={{
						display: "grid",
						gap: 2,
						gridTemplateColumns: {
							xs: "1fr",
							md: "repeat(2, minmax(0, 1fr))"
						}
					}}
				>
					<Box sx={{ display: "grid", gap: 2 }}>
						<TextField
							label="Funding Source"
							select
							value={fundingSource}
							onChange={(e) =>
								setFundingSource(
									e.target.value as "partner" | "shopCash"
								)
							}
						>
							<MenuItem value={"partner"}>Partner</MenuItem>
							<MenuItem value={"shopCash"}>Shop Cash</MenuItem>
						</TextField>
						{fundingSource === "partner" ? (
							<TextField
								label="Partner"
								value={partnerName}
								onChange={(event) =>
									setPartnerName(event.target.value)
								}
								select
								SelectProps={{ native: true }}
							>
								<option value="">Select or type partner</option>
								{partnerOptions.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</TextField>
						) : (
							// ShopCash picker
							<Autocomplete
								options={cashMovements.filter(
									(m: any) =>
										m.type === "EXPENSE" &&
										m.expenseCategory ===
											"Stock Purchase" &&
										!investments.some(
											(inv: any) =>
												inv.shopCashRef === m.id
										)
								)}
								getOptionLabel={(option: any) =>
									`${formatDate(option.movementDate)} - ${option.description || option.expenseCategory || ""} - Rs. ${option.amount}`
								}
								onChange={(_e, newValue) =>
									setShopCashRef(newValue?.id || "")
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Link ShopCash expense"
									/>
								)}
							/>
						)}
						<TextField
							label="Investment Amount"
							type="number"
							value={amount}
							onChange={(event) => setAmount(event.target.value)}
						/>
						<TextField
							label="Investment Date"
							type="date"
							disabled={!isAdmin}
							value={investmentDate}
							onChange={(event) =>
								setInvestmentDate(event.target.value)
							}
							InputLabelProps={{ shrink: true }}
						/>
						<TextField
							label="Description"
							value={description}
							onChange={(event) =>
								setDescription(event.target.value)
							}
							multiline
							minRows={2}
						/>
					</Box>
					<Box sx={{ display: "grid", gap: 2 }}>
						{itemRows.map((row, idx) => (
							<Box
								key={idx}
								sx={{
									display: "flex",
									gap: 1,
									alignItems: "center"
								}}
							>
								<Autocomplete
									sx={{ flex: 1, minWidth: 340 }}
									options={items}
									getOptionLabel={(option: any) =>
										`(${option.itemCode}) ${option.name}`
									}
									value={
										items.find(
											(it) => it.key === row.itemKey
										) || null
									}
									onChange={(_e, newValue) => {
										const copy = [...itemRows];
										copy[idx] = {
											...copy[idx],
											itemKey: newValue?.key || ""
										};
										setItemRows(copy);
									}}
									filterOptions={(
										options,
										{ inputValue }
									) => {
										return options.filter(
											(option: any) =>
												option.name
													.toLowerCase()
													.includes(
														inputValue.toLowerCase()
													) ||
												option.itemCode
													.toLowerCase()
													.includes(
														inputValue.toLowerCase()
													)
										);
									}}
									getOptionDisabled={(option: any) =>
										investments.some(
											(inv) =>
												getComputedStatus(inv) ===
													"active" &&
												inv.itemLinks?.some(
													(l) =>
														l.itemKey === option.key
												)
										)
									}
									renderInput={(params) => (
										<TextField
											{...params}
											label={`Item ${idx + 1}`}
											fullWidth
										/>
									)}
								/>
								{/* Quantity and cost basis are computed automatically; no inputs shown */}
								<IconButton
									onClick={() => {
										const copy = [...itemRows];
										copy.splice(idx, 1);
										setItemRows(
											copy.length
												? copy
												: [{ itemKey: "" }]
										);
									}}
									aria-label="remove"
								>
									<DeleteIcon />
								</IconButton>
							</Box>
						))}
						<Box
							sx={{
								display: "flex",
								gap: 1,
								alignItems: "center"
							}}
						>
							<Button
								startIcon={<AddIcon />}
								onClick={() =>
									setItemRows([...itemRows, { itemKey: "" }])
								}
							>
								Add item
							</Button>
							<Button
								variant="contained"
								onClick={handleSave}
								disabled={saving}
							>
								{saving ? "Saving..." : "Save Investment"}
							</Button>
							{savingMessage ? (
								<Typography variant="body2">
									{savingMessage}
								</Typography>
							) : null}
						</Box>
					</Box>
				</Box>
			</Paper>

			<Paper sx={{ p: 3 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
						flexWrap: "wrap",
						gap: 2
					}}
				>
					<Typography variant="h6">Summary</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
						<Chip
							label={`Total invested: ${formatCurrency(summary.totalInvested)}`}
							color="primary"
						/>
						{/* <Chip
							label={`Outstanding: ${formatCurrency(summary.totalOutstanding)}`}
							color="secondary"
						/> */}
						<Chip
							label={`Active entries: ${summary.activeCount}`}
						/>
					</Box>
				</Box>
				<Box
					sx={{
						display: "grid",
						gap: 2,
						gridTemplateColumns: {
							xs: "1fr",
							md: "repeat(2, minmax(0, 1fr))"
						}
					}}
				>
					{Object.entries(summary.partnerBreakdown).map(
						([partner, amount]) => (
							<Paper
								key={partner}
								variant="outlined"
								sx={{ p: 2 }}
							>
								<Typography variant="subtitle2">
									{partner}
								</Typography>
								<Typography variant="body1">
									{formatCurrency(amount)}
								</Typography>
							</Paper>
						)
					)}
				</Box>
			</Paper>

			<Paper sx={{ p: 3 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
						flexWrap: "wrap",
						gap: 2
					}}
				>
					<Typography variant="h6">Investment History</Typography>
					<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
						<TextField
							label="Start Date"
							type="date"
							disabled={!isAdmin}
							value={startDate}
							onChange={(event) =>
								setStartDate(event.target.value)
							}
							InputLabelProps={{ shrink: true }}
						/>
						<TextField
							label="End Date"
							type="date"
							disabled={!isAdmin}
							value={endDate}
							onChange={(event) => setEndDate(event.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
						<Button variant="outlined" onClick={handleSearch}>
							Search
						</Button>
					</Box>
				</Box>
				{loading ? (
					<Typography>Loading investments...</Typography>
				) : null}
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Date</TableCell>
								<TableCell>Partner</TableCell>
								<TableCell>Amount</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Item</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{investments.map((investment) => (
								<TableRow key={investment.id}>
									<TableCell>
										{formatDate(investment.investmentDate)}
									</TableCell>
									<TableCell>
										{investment.fundingSource ===
										"shopCash" ? (
											<Chip
												label={getShopCashLabel(
													investment
												)}
												color="primary"
												size="small"
											/>
										) : (
											investment.partnerName
										)}
									</TableCell>
									<TableCell>
										{formatCurrency(investment.amount)}
									</TableCell>
									<TableCell>
										{getComputedStatus(investment)}
									</TableCell>
									<TableCell>
										<Box
											sx={{
												display: "flex",
												gap: 1,
												alignItems: "center"
											}}
											onMouseEnter={(event) =>
												investment.itemLinks?.length
													? openItemPopover(
															event,
															investment.itemLinks
														)
													: undefined
											}
											onMouseLeave={closeItemPopover}
										>
											<Typography>
												{investment.itemLinks?.[0]
													?.itemName || "-"}
											</Typography>
											{investment.itemLinks?.length >
											1 ? (
												<Typography
													component="span"
													sx={{
														color: "text.secondary",
														fontStyle: "italic"
													}}
												>
													+
													{investment.itemLinks
														.length - 1}{" "}
													more
												</Typography>
											) : null}
										</Box>
									</TableCell>
									<TableCell>
										{
											<Button
												size="small"
												color="error"
												startIcon={<DeleteIcon />}
												disabled={!isAdmin}
												onClick={async () => {
													if (
														!window.confirm(
															"Delete this investment? This will remove the investment and clear linked items."
														)
													)
														return;
													try {
														await deletePartnerInvestment(
															investment.id
														);
														await fetchPartnerInvestments(
															startDate,
															endDate,
															items
														);
													} catch (error) {
														console.error(
															"Failed to delete investment:",
															error
														);
														alert(
															"Failed to delete investment."
														);
													}
												}}
											>
												Delete
											</Button>
										}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
				<Popover
					open={itemPopoverOpen}
					anchorEl={itemPopoverAnchor}
					anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
					transformOrigin={{ vertical: "top", horizontal: "left" }}
					onClose={() => setItemPopoverAnchor(null)}
					disableRestoreFocus
					PaperProps={{
						sx: { p: 2, minWidth: 280, maxWidth: 360 }
					}}
				>
					<Box
						onMouseEnter={cancelClosePopover}
						onMouseLeave={closeItemPopover}
					>
						<Box
							sx={{
								display: "flex",
								gap: 1,
								alignItems: "center",
								mb: 1
							}}
						>
							<Typography variant="subtitle2">
								Linked items
							</Typography>
							<Button
								size="small"
								startIcon={<ContentCopyIcon />}
								onClick={handleCopyItems}
							>
								Copy all
							</Button>
						</Box>
						{activeItemLinks.map((link, index) => (
							<Typography
								key={index}
								variant="body2"
								sx={{ mb: 0.5 }}
							>
								{link.itemCode ? `(${link.itemCode}) ` : ""}
								{link.itemName || "Unnamed item"}x
								{link.quantity ?? 0}
								{link.itemPurchasePrice != null
									? ` | ${formatCurrency(link.itemPurchasePrice)}`
									: ""}
							</Typography>
						))}
						{copyMessage ? (
							<Typography variant="caption" color="success.main">
								{copyMessage}
							</Typography>
						) : null}
					</Box>
				</Popover>
			</Paper>
		</Box>
	);
};
